import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  addSkiResort,
  deleteSkiResort,
  fetchSkiResortDetails,
  fetchSkiResorts,
  updateSkiResort,
} from '../../common/api/skiResortsService';
import { RootState } from '../../app/store';
import { LOADING_STATUS } from '../../common/constants';
import { TranslationItem } from '../../common/types/translations.types';
import {
  InitialPaginationResponse,
  PaginationParameters,
  PaginationResponse,
  PopulatePagination,
} from '../../common/types/pagination.types';

export interface SkiResortDetails {
  id?: string;
  isDeleted?: boolean;
  latitude: string;
  longitude: string;
  regionId: string;
  nameTranslations: TranslationItem[];
  descriptionTranslations: TranslationItem[];
  metaTitleTranslations: TranslationItem[];
  metaDescriptionTranslations: TranslationItem[];
}

export interface SkiResortListItem {
  id: string;
  name: string;
}

export interface SiteListItem {
  id: string;
  name: string;
}

export interface SkiResortsFilter extends PaginationParameters {
  searchString: string;
}

export interface SkiResortsState {
  skiResortItems: SkiResortListItem[];
  skiResortDetails: SkiResortDetails;
  skiResortItemsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  skiResortDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  skiResortItemsPagination: PaginationResponse;
}

const initialState: SkiResortsState = {
  skiResortItems: [],
  skiResortDetails: {
    id: '',
    isDeleted: false,
    latitude: '',
    longitude: '',
    regionId: '',
    nameTranslations: [],
    descriptionTranslations: [],
    metaTitleTranslations: [],
    metaDescriptionTranslations: [],
  },
  skiResortItemsStatus: LOADING_STATUS.IDLE,
  skiResortDetailsStatus: LOADING_STATUS.IDLE,
  skiResortItemsPagination: InitialPaginationResponse,
};

export const getSkiResortsAsync = createAsyncThunk(
  'skiResorts/getSkiResorts',
  async (skiResortsFilter: SkiResortsFilter, { rejectWithValue }) => {
    try {
      const response = await fetchSkiResorts(skiResortsFilter);
      return response;
    } catch (error) {
      return rejectWithValue(error.response);
    }
  },
);

export const getSkiResortDetailsAsync = createAsyncThunk(
  'skiResorts/getSkiResortDetails',
  async (skiResortId: string, { rejectWithValue }) => {
    try {
      const response = await fetchSkiResortDetails(skiResortId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const saveSkiResortAsync = createAsyncThunk(
  'skiResorts/addSkiResort',
  async (skiResortData: SkiResortDetails, { rejectWithValue }) => {
    try {
      const response = await addSkiResort(skiResortData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateSkiResortAsync = createAsyncThunk(
  'skiResorts/updateSkiResort',
  async (skiResortData: SkiResortDetails, { rejectWithValue }) => {
    try {
      const response = await updateSkiResort(skiResortData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteSkiResortAsync = createAsyncThunk(
  'skiResorts/deleteSkiResort',
  async (skiResortId: string, { rejectWithValue }) => {
    try {
      const response = await deleteSkiResort(skiResortId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const skiResortsSlice = createSlice({
  name: 'skiResorts',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getSkiResortsAsync.pending, state => {
        state.skiResortItemsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getSkiResortsAsync.fulfilled, (state, action) => {
        state.skiResortItemsStatus = LOADING_STATUS.IDLE;
        state.skiResortItems = action.payload.data;
        state.skiResortItemsPagination = PopulatePagination(action.payload);
      })
      .addCase(getSkiResortsAsync.rejected, state => {
        state.skiResortItemsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getSkiResortDetailsAsync.pending, state => {
        state.skiResortDetailsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getSkiResortDetailsAsync.fulfilled, (state, action) => {
        state.skiResortDetailsStatus = LOADING_STATUS.IDLE;
        state.skiResortDetails = action.payload;
      })
      .addCase(getSkiResortDetailsAsync.rejected, state => {
        state.skiResortDetailsStatus = LOADING_STATUS.FAILED;
      });
  },
});

export const selectSkiResortItems = (state: RootState) =>
  state.skiResorts.skiResortItems;
export const selectSkiResortItemsPagination = (state: RootState) =>
  state.skiResorts.skiResortItemsPagination;
export const selectSkiResortItemsLoadingStatus = (state: RootState) =>
  state.skiResorts.skiResortItemsStatus;
export const selectSkiResortDetails = (state: RootState) =>
  state.skiResorts.skiResortDetails;
export const selectSkiResortDetailsLoadingStatus = (state: RootState) =>
  state.skiResorts.skiResortDetailsStatus;

export default skiResortsSlice.reducer;
